import { skipToken } from '@reduxjs/toolkit/query'
import { LayoutList } from 'containers/Layout'
import { InfoWidget } from 'containers/Widgets'
import { WidgetDateFilter } from 'containers/Widgets/types'
import { WelcomeWidget } from 'containers/Widgets/WelcomeWidget/WelcomeWidget'
import React, { useState } from 'react'
import { useSelector } from 'react-redux'
import { AuthState, selectClientId, selectClientModules } from 'services/slices/auth'
import {
  useGetWidgetInfoQuery,
  useGetWidgetPTWQuery,
  useGetWidgetRankQuery,
} from 'services/Widgets/widgets'
import { useTranslation } from 'utils/useTranslation'
import {
  engLayout,
  MRFLayout,
  opsLayout,
  opsMRFLayout,
  StaticWidgetLayoutItem,
} from 'pages/Home/homeLayout'
import { RankWidget } from 'containers/Widgets/RankWidget'
import { DateFilters } from 'containers/Widgets/Atoms/DateFilters'
import { PartToWholeWidget } from 'containers/Widgets/PartToWholeWidget'
import styled from 'styled-components'
import { CheckCircle } from '@evrekadev/evreka-ui-components'
import { transformers, WidgetTransformer } from './Home/transformers'
import { useGetMrfLabelsQuery } from 'services/common'

const getLayout = (modules: AuthState['available_modules'], clientId?: number) => {
  const clientConfig = window.widgetConfig?.clients?.find((c) => c.clientId === clientId)
  if (!clientConfig) {
    return []
  }
  let layout: Array<Omit<StaticWidgetLayoutItem, 'currentFilter'>> = []

  if (
    modules.ops &&
    clientConfig.modules.includes('ops') &&
    (!modules.mrf || !clientConfig.modules.includes('mrf'))
  ) {
    layout = opsLayout
  } else if (
    modules.ops &&
    clientConfig.modules.includes('ops') &&
    modules.mrf &&
    clientConfig.modules.includes('mrf')
  ) {
    layout = opsMRFLayout
  } else if (modules.mrf && clientConfig.modules.includes('mrf')) {
    layout = MRFLayout
  } else {
    layout = []
  }

  if (modules.engagement && clientConfig.modules.includes('eng')) {
    layout = [...layout, ...engLayout]
  }
  return layout
}

export const Home: React.FC = () => {
  const { t } = useTranslation('pages.widgets')
  const modules = useSelector(selectClientModules)
  const clientId = useSelector(selectClientId)
  const [currentFilter, setCurrentFilter] = useState<WidgetDateFilter>('1m')

  const [showZero, setShowZero] = useState(true)

  const layout = getLayout(modules, clientId)

  return (
    <LayoutList>
      <div
        style={{
          marginBottom: '1rem',
          width: 'fit-content',
          display: 'flex',
          gap: '1rem',
          alignItems: 'center',
        }}
      >
        <DateFilters onChange={setCurrentFilter} value={currentFilter} />
        <div style={{ width: '600px' }}>
          <CheckCircle
            componentSize="small"
            checked={showZero}
            onChange={(e) => setShowZero(e.target.checked)}
            text={t('show_zero')}
          />
        </div>
      </div>
      <HomeGrid>
        <WelcomeWidget />
        {layout.map((item) => (
          <Widget
            key={item.widgetId}
            type={item.type}
            currentFilter={currentFilter}
            widgetId={item.widgetId}
            size={item.size}
            showZero={showZero}
            transformer={transformers[item.widgetId]}
          />
        ))}
      </HomeGrid>
    </LayoutList>
  )
}

const HomeGrid = styled.div`
  display: grid;
  grid-auto-rows: 200px;
  gap: 1rem;
  grid-template-columns: repeat(5, 1fr);
  grid-auto-flow: dense;

  transition: grid-template-columns 0.5s ease;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(4, 1fr);
  }

  @media (max-width: 992px) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }
`

const Widget: React.FC<
  StaticWidgetLayoutItem & { load?: boolean; showZero?: boolean; transformer?: WidgetTransformer }
> = ({ type, ...rest }) => {
  switch (type) {
    case 'info':
      return <InfoWidgetWithData {...rest} />
    case 'rank':
      return <RankWidgetWithData {...rest} />
    case 'part-to-whole':
      return <PTWWidgetWithData {...rest} />
    default:
      return null
  }
}

const InfoWidgetWithData: React.FC<
  Omit<StaticWidgetLayoutItem, 'type'> & { load?: boolean; showZero?: boolean }
> = ({ widgetId, currentFilter, size, load, showZero }) => {
  const { t } = useTranslation('pages.widgets.titles')
  const clientId = useSelector(selectClientId)

  // ops:route:average_distance
  const [module, submodule, widget] = widgetId.split(':')

  const label = useMRFLabel({ module, submodule, clientId })

  const { data, isLoading, isFetching, refetch } = useGetWidgetInfoQuery(
    clientId
      ? {
          clientId,
          module,
          submodule,
          widget,
          period: currentFilter,
          historic: size !== '1x1',
        }
      : skipToken,
  )
  return (
    <InfoWidget
      title={t(widget, { id: label })}
      data={data}
      loading={load || isLoading}
      fetching={isFetching}
      currentFilter={currentFilter}
      size={size}
      showZero={showZero}
      debug={refetch}
    />
  )
}

const RankWidgetWithData: React.FC<Omit<StaticWidgetLayoutItem, 'type'>> = ({
  widgetId,
  size,
  currentFilter,
}) => {
  const { t } = useTranslation('pages.widgets.titles')
  const clientId = useSelector(selectClientId)

  // ops:route:average_distance
  const [module, submodule, widget] = widgetId.split(':')

  const label = useMRFLabel({ module, submodule, clientId })

  const { data, isLoading, isFetching } = useGetWidgetRankQuery(
    clientId
      ? {
          clientId,
          module,
          submodule,
          widget,
          period: currentFilter,
        }
      : skipToken,
  )

  return (
    <RankWidget
      title={t(widget, { id: label })}
      data={data}
      loading={isLoading || isFetching}
      currentFilter={currentFilter}
      size={size}
    />
  )
}

const PTWWidgetWithData: React.FC<
  Omit<StaticWidgetLayoutItem, 'type'> & {
    transformer?: WidgetTransformer
  }
> = ({ widgetId, size, currentFilter, transformer }) => {
  const { t } = useTranslation('pages.widgets.titles')
  const clientId = useSelector(selectClientId)

  // ops:route:average_distance
  const [module, submodule, widget] = widgetId.split(':')

  const label = useMRFLabel({ module, submodule, clientId })

  const {
    data: dataResponse,
    isLoading,
    isFetching,
  } = useGetWidgetPTWQuery(
    clientId
      ? {
          clientId,
          module,
          submodule,
          widget,
          period: currentFilter,
        }
      : skipToken,
  )

  const data =
    transformer?.type === 'part-to-whole'
      ? dataResponse
        ? transformer.transformer(dataResponse)
        : undefined
      : dataResponse

  return (
    <PartToWholeWidget
      title={t(widget, { id: label })}
      data={data}
      loading={isLoading || isFetching}
      currentFilter={currentFilter}
      size={size}
    />
  )
}

const useMRFLabel = (args: { module: string; submodule: string; clientId?: number }) => {
  const { data: labels } = useGetMrfLabelsQuery(
    args.module === 'mrf' && args.clientId ? args.clientId : skipToken,
  )

  const foundLabel = labels?.find(
    (l) => l.value === (args.submodule === 'outbound' ? 'consignment' : args.submodule),
  )

  return foundLabel?.label
}
